// Chart-specific formatting for the reporting bar chart: a fixed categorical
// palette, series folding/naming, and compact axis ticks. Kept separate from
// formatValue.ts (which the table and CSV also use) so the chart can shape
// series without affecting those surfaces.
import type { ResultRow } from './arrowToRows';
import {
  humanizeHeader,
  humanizePivotValue,
  resolveFormatKind,
  type FormatKind,
} from './formatValue';
import type { DatasetColumn } from '../types/reporting';

// Categorical palette, fixed order, light mode. Assigned by series position and
// never cycled — past 8 series the tail folds into "Other" or drops, never a 9th
// generated hue (see resolveSeries). Validated
// with the dataviz palette checker (lightness band, chroma floor, CVD and
// normal-vision separation all pass).
export const CATEGORICAL_PALETTE = [
  '#2a78d6',
  '#008300',
  '#e87ba4',
  '#eda100',
  '#1baf7a',
  '#eb6834',
  '#4a3aa7',
  '#e34948',
] as const;

export const OTHER_KEY = '__other';

// Eight is the palette size, so up to eight series each get their own hue. Past
// eight: an additive, single-unit tail folds into seven real series + one
// "Other" (the eighth color); a non-additive/mixed-unit tail can't be summed, so
// the eight largest series show individually and the rest drop (see resolveSeries).
const MAX_SERIES = 8;
const TOP_SERIES_WHEN_FOLDING = MAX_SERIES - 1;

// Cardinality ceiling: past this many X categories the chart both becomes
// unreadable and risks freezing the browser (each category is its own set of
// SVG bar nodes). An additive measure lets us keep the top-N by total and
// disclose the rest; a non-additive one makes "top by total" misleading, so the
// chart declines to render and points at the table instead (see capCategories).
export const MAX_CATEGORIES = 50;

// Date-axis ceiling: a chronological (date / date_trunc) X axis gets a higher cap
// than the generic categorical one because a time series has two properties an
// arbitrary group-by does not. First, chronology IS the meaning of a date axis —
// the left-to-right order is the information, so the "top-N by total" ranking
// capCategories applies to a rankable categorical set is ALWAYS misleading here
// (it would drop and reorder days by size, scrambling the timeline). Second, a
// single ordered date series is trivial SVG load, so the 50-cap's browser-freeze
// rationale (unbounded categorical group-bys, each its own bar-node set) doesn't
// apply. 92 ≈ a quarter of daily buckets; the existing -45/-90 tick rotation and
// `interval` thinning keep that many upright ticks legible. Past 92 the chart
// declines (overflow) rather than re-rank — see capCategories' date-axis branch.
export const MAX_DATE_CATEGORIES = 92;

// Aggregate tokens that introduce a measure alias. "count" is a lone-token alias
// ("count", "<value>_count"); the rest carry a column ("sum_balance",
// "<value>_avg_total_charges").
const AGG_MEASURES = new Set(['sum', 'avg', 'min', 'max']);

// Aggregates whose values are additive across categories, so summing a folded
// tail preserves the measure's meaning. count and sum add up; avg/min/max (and
// any non-aggregate measure) do not — averaging averages or summing minimums is
// mathematically wrong.
const ADDITIVE_AGGS = new Set(['count', 'sum']);

/**
 * True when a measure alias is additive across rows/categories. "count" and any
 * "sum_<column>" fold cleanly by summing; "avg_/min_/max_" and bare measures do
 * not.
 */
function isAdditiveAlias(alias: string): boolean {
  if (alias === 'count') return true;
  const agg = alias.slice(0, alias.indexOf('_'));
  return ADDITIVE_AGGS.has(agg);
}

export interface ChartSeries {
  key: string;
  name: string;
  color: string;
  /** Format kind for this series' values (money vs plain number). */
  kind: FormatKind;
  /**
   * True only for a whole-row `count` measure, whose values are always integers.
   * A generic numeric measure (avg/min/max, or a sum of a non-money column) can
   * be fractional, so it must NOT force an integer-only axis — averaging days
   * outstanding yields 12.4, and a narrow decimal range needs decimal ticks.
   */
  integerValued: boolean;
}

/** A count measure ("count" / "<value>_count") yields integer values only. */
function isCountAlias(alias: string): boolean {
  return alias === 'count';
}

/** Separator joining multiple group-by dimensions into one composite X label. */
export const CATEGORY_SEP = ' · ';

// A series' values are either money (dollar axis) or a plain count/number
// (unitless axis). Mixing both on one linear axis makes the smaller-scaled
// series (usually counts) render as an invisible sliver, so each unit gets its
// own Y axis keyed by this id.
export type ChartAxisId = 'money' | 'count';

export function axisIdForKind(kind: FormatKind): ChartAxisId {
  return kind === 'money' ? 'money' : 'count';
}

/** Which unit axes the series need, so the chart renders only the axes in use. */
export function seriesUnits(series: ChartSeries[]): {
  hasMoney: boolean;
  hasCount: boolean;
} {
  return {
    hasMoney: series.some(s => s.kind === 'money'),
    hasCount: series.some(s => s.kind !== 'money'),
  };
}

/**
 * True when the (non-money) count axis carries only whole-row count measures, so
 * fractional ticks (0.5, 1.5) are always meaningless and the axis can force
 * integer ticks. False when any non-money series can be fractional (avg/min/max,
 * or a sum of a non-money column), so a report averaging below 1 or over a narrow
 * decimal range keeps a proper decimal scale instead of a coarse integer one.
 */
export function countAxisIsIntegerOnly(series: ChartSeries[]): boolean {
  const countAxis = series.filter(s => s.kind !== 'money');
  return countAxis.length > 0 && countAxis.every(s => s.integerValued);
}

interface ParsedSeries {
  /** Pivot-value tokens, or "" for a plain (non-pivoted) grouped measure. */
  prefix: string;
  /** Measure alias: "count" or "<agg>_<column>". */
  alias: string;
}

function seriesTotal(rows: ResultRow[], key: string): number {
  let total = 0;
  for (const row of rows) {
    const value = row[key];
    if (typeof value === 'number') total += value;
  }
  return total;
}

/**
 * Split a measure column into its pivot-value prefix and measure alias. DuckDB
 * pivot output is `<pivotValue>_<measureAlias>` ("not_started_count",
 * "denied_sum_balance"); a plain grouped measure has no prefix ("count",
 * "sum_balance"). Scans right-to-left so a pivot value that itself contains an
 * agg-like word still yields the trailing measure. Returns the whole key as the
 * alias when no measure token is found (leaves it to humanizeHeader / number).
 */
function splitMeasure(key: string): ParsedSeries {
  if (key === 'count') return { prefix: '', alias: 'count' };
  const tokens = key.split('_');
  for (let i = tokens.length - 1; i >= 0; i--) {
    const token = tokens[i];
    if (token === 'count' && i === tokens.length - 1) {
      return { prefix: tokens.slice(0, i).join('_'), alias: 'count' };
    }
    if (AGG_MEASURES.has(token) && i < tokens.length - 1) {
      return {
        prefix: tokens.slice(0, i).join('_'),
        alias: tokens.slice(i).join('_'),
      };
    }
  }
  return { prefix: '', alias: key };
}

/**
 * Build a per-series name resolver. A single-measure pivot repeats the same
 * alias on every column, so we show the category value alone ("Completed"). A
 * multi-measure pivot names each cross by value AND measure
 * ("Denied · Sum of Balance") so distinct measures never collide. A plain
 * grouped measure humanizes the alias itself ("Sum of Balance").
 */
function seriesNamer(
  parsed: ParsedSeries[],
  columnLabels?: Map<string, string>,
  columnMeta?: Map<string, DatasetColumn>,
  pivotColumn?: string
): (index: number, key: string) => string {
  const singleMeasure = new Set(parsed.map(p => p.alias)).size === 1;
  const allPivoted = parsed.every(p => p.prefix !== '');
  // Pivot value tokens map through the pivot column's display formatter (so a
  // claim_type pivot legend reads "Medicare Part A", not "MA"); the measure alias
  // keeps its generic humanized name ("Sum of Balance").
  const valueLabel = (token: string) =>
    humanizePivotValue(token, pivotColumn, columnMeta);
  const measureLabel = (token: string) =>
    humanizeHeader(token, columnLabels, columnMeta);
  return (index, key) => {
    const { prefix, alias } = parsed[index];
    if (allPivoted && singleMeasure) return valueLabel(prefix);
    if (prefix) return `${valueLabel(prefix)} · ${measureLabel(alias)}`;
    return measureLabel(key);
  };
}

/**
 * Resolve the measure columns into ordered chart series with fixed-order colors,
 * humanized names, and per-series format kind. Past eight series, an additive
 * single-unit tail folds into a summed "Other"; a non-additive or mixed-unit
 * tail can't be summed, so the eight largest show individually and the rest drop
 * (never a ninth generated hue). Money detection and naming come from each
 * series' extracted measure alias, so heterogeneous multi-measure pivots and
 * lone pivot series are classified correctly. Returns rows augmented with the
 * folded total when (and only when) folding happens, plus `hiddenSeriesCount`:
 * how many series were dropped (the non-foldable tail beyond the palette) so the
 * chart can disclose that it isn't showing everything.
 */
export function resolveSeries(
  rows: ResultRow[],
  seriesKeys: string[],
  columnLabels?: Map<string, string>,
  columnMeta?: Map<string, DatasetColumn>,
  pivotColumn?: string
): { series: ChartSeries[]; rows: ResultRow[]; hiddenSeriesCount: number } {
  const parsed = seriesKeys.map(splitMeasure);
  const nameFor = seriesNamer(parsed, columnLabels, columnMeta, pivotColumn);
  // Resolve the format kind from the extracted alias ("sum_balance"), not the
  // full pivot key ("north_sum_balance") whose leading category token would hide
  // the money column from isMoneyColumn and drop currency formatting.
  const kindFor = (index: number) =>
    resolveFormatKind(parsed[index].alias, columnMeta);
  const build = (key: string, index: number, color: string): ChartSeries => ({
    key,
    color,
    name: nameFor(index, key),
    kind: kindFor(index),
    integerValued: isCountAlias(parsed[index].alias),
  });

  if (seriesKeys.length <= MAX_SERIES) {
    return {
      series: seriesKeys.map((key, i) => build(key, i, CATEGORICAL_PALETTE[i])),
      rows,
      hiddenSeriesCount: 0,
    };
  }

  // More series than the fixed palette allows: rank by total; the smallest fall
  // out of the eight-hue palette. A ninth-or-later series must never earn a
  // generated hue, but the *tail* can only fold into a summed "Other" when doing
  // so is mathematically sound — every folded series must be an additive
  // aggregate (count/sum) sharing one unit (all money or all plain). A tail that
  // mixes non-additive aggregates (avg/min/max) or units (counts + dollars)
  // can't be summed into one meaningful bar.
  const indexed = seriesKeys.map((key, i) => ({ key, i }));
  const ranked = [...indexed].sort(
    (a, b) => seriesTotal(rows, b.key) - seriesTotal(rows, a.key)
  );
  const foldCandidates = ranked.slice(TOP_SERIES_WHEN_FOLDING);
  const foldable =
    foldCandidates.every(({ i }) => isAdditiveAlias(parsed[i].alias)) &&
    new Set(foldCandidates.map(({ i }) => kindFor(i))).size === 1;

  if (!foldable) {
    // Can't sum the tail meaningfully. Show the eight largest real series each
    // in its own prescribed color (fixed order by original series position) and
    // drop the rest — the full data for every series still shows in the table.
    // Silently dropping beats inventing a bogus summed bucket.
    const kept = new Set(ranked.slice(0, MAX_SERIES).map(r => r.key));
    const series = indexed
      .filter(({ key }) => kept.has(key))
      .map(({ key, i }, position) =>
        build(key, i, CATEGORICAL_PALETTE[position])
      );
    return {
      series,
      rows,
      hiddenSeriesCount: seriesKeys.length - MAX_SERIES,
    };
  }

  // Additive, single-unit tail: keep the top seven and sum the rest per row.
  const kept = new Set(
    ranked.slice(0, TOP_SERIES_WHEN_FOLDING).map(r => r.key)
  );
  const keptOrdered = indexed.filter(({ key }) => kept.has(key));
  const folded = seriesKeys.filter(key => !kept.has(key));

  const foldedRows = rows.map(row => {
    let other = 0;
    for (const key of folded) {
      const value = row[key];
      if (typeof value === 'number') other += value;
    }
    return { ...row, [OTHER_KEY]: other };
  });

  const series = keptOrdered.map(({ key, i }, position) =>
    build(key, i, CATEGORICAL_PALETTE[position])
  );
  // Other takes the eighth prescribed color (never a neutral/off-palette fill)
  // so the fixed categorical order holds. The tail is homogeneous by the
  // foldable check, so its unit is the kind of any folded series.
  series.push({
    key: OTHER_KEY,
    color: CATEGORICAL_PALETTE[MAX_SERIES - 1],
    name: 'Other',
    kind: kindFor(foldCandidates[0].i),
    // Integer only when every folded series is a count; a folded sum of a
    // non-money column stays fractional-capable.
    integerValued: foldCandidates.every(({ i }) =>
      isCountAlias(parsed[i].alias)
    ),
  });
  return { series, rows: foldedRows, hiddenSeriesCount: 0 };
}

/**
 * True when every measure column is additive across categories (count / sum), so
 * summing a category's rows preserves the measure's meaning. An avg/min/max (or
 * bare) measure is not additive.
 */
export function seriesKeysAdditive(seriesKeys: string[]): boolean {
  return seriesKeys.every(key => isAdditiveAlias(splitMeasure(key).alias));
}

/**
 * True when a category set can be ranked by summed total and truncated to a
 * top-N without misleading. Two conditions must hold: every measure is additive
 * (count / sum), AND every series measures the *same* additive quantity — i.e.
 * shares one measure alias, which is exactly a pivot of one measure across
 * categories (all `count`: "completed_count" + "not_started_count"; or all
 * `sum_balance`: "north_sum_balance" + "south_sum_balance"). Summing across
 * those pivot columns re-totals one real quantity, so ranking by it is sound.
 *
 * Distinct aliases are never safe to sum for ranking, even when both are
 * additive: "count" + "sum_balance" mixes claims and dollars (dollar magnitude
 * silently dominates), and "count" + "sum_days_outstanding" mixes claims and
 * days (a patient with one 100-day claim would outrank one with 50 new claims).
 * A money-vs-count axis test can't catch the days case — count and days share
 * the non-money axis yet are incompatible units — so the guard keys off the
 * measure alias itself, not the coarse axis unit. Any distinct-alias or
 * non-additive set defers to the table instead of truncating.
 */
export function seriesKeysRankableByTotal(seriesKeys: string[]): boolean {
  if (!seriesKeysAdditive(seriesKeys)) return false;
  const measures = new Set(seriesKeys.map(key => splitMeasure(key).alias));
  return measures.size <= 1;
}

export interface CategoryCap {
  /** Rows to chart: unchanged under the ceiling, else the top-N by total. */
  rows: ResultRow[];
  /** How many categories were dropped (0 when under the ceiling). */
  hiddenCategoryCount: number;
  /**
   * True when the category count exceeds the ceiling but the measures can't be
   * ranked by total (non-additive, or a mixed money+count set), so a top-N is
   * misleading — the chart shows a "use the table" notice instead of rendering.
   */
  overflow: boolean;
}

/**
 * Enforce the category cardinality ceiling. Under `limit` categories the rows
 * pass through untouched. Above it, a rankable measure set (additive AND a
 * single measure alias — see seriesKeysRankableByTotal) keeps the `limit`
 * largest categories by summed total (and reports the rest as hidden); a set
 * that can't be ranked by total — non-additive, or mixing distinct measures
 * (dollars + counts, or claims + days) — sets `overflow` so the caller renders a
 * table-instead notice rather than a misleading top-N.
 *
 * `dateAxis` switches to a chronology-preserving mode for a date / date_trunc X
 * axis: the ceiling is MAX_DATE_CATEGORIES and the top-N-by-total ranking below
 * is NEVER reachable — over the cap the axis overflows unconditionally, even for
 * a rankable measure set, because re-ranking a timeline by size destroys the
 * order that is its meaning (see MAX_DATE_CATEGORIES). Rows pass through in their
 * original ORDER BY (chronological) order under the cap.
 */
export function capCategories(
  rows: ResultRow[],
  seriesKeys: string[],
  limit = MAX_CATEGORIES,
  dateAxis = false
): CategoryCap {
  if (dateAxis) {
    if (rows.length <= MAX_DATE_CATEGORIES) {
      return { rows, hiddenCategoryCount: 0, overflow: false };
    }
    // Too many days to chart legibly. Never rank a date axis by total (the
    // top-N path below would scramble chronology), so overflow to the table
    // regardless of whether the measures are rankable.
    return { rows, hiddenCategoryCount: rows.length, overflow: true };
  }
  if (rows.length <= limit) {
    return { rows, hiddenCategoryCount: 0, overflow: false };
  }
  if (!seriesKeysRankableByTotal(seriesKeys)) {
    return { rows, hiddenCategoryCount: rows.length, overflow: true };
  }
  const rowTotal = (row: ResultRow): number => {
    let total = 0;
    for (const key of seriesKeys) {
      const value = row[key];
      if (typeof value === 'number') total += value;
    }
    return total;
  };
  const kept = [...rows]
    .sort((a, b) => rowTotal(b) - rowTotal(a))
    .slice(0, limit);
  return {
    rows: kept,
    hiddenCategoryCount: rows.length - limit,
    overflow: false,
  };
}

const numberCompact = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const moneyCompact = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/** Compact axis tick ("1.2K", "$1.2M"); passes non-numbers through unchanged. */
export function compactTick(value: unknown, money: boolean): string {
  if (typeof value !== 'number') return String(value ?? '');
  return money ? moneyCompact.format(value) : numberCompact.format(value);
}

/** Truncate a long category label for axis display; tooltip shows the full value. */
export function truncateLabel(text: string, max = 14): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

// Past this many categories, vertical bars force -45/-90 tick rotation (the real
// readability problem the critique flagged), so the chart flips to horizontal
// and puts category names fully upright on the Y axis instead.
export const HORIZONTAL_CATEGORY_THRESHOLD = 8;
// With several categories present, labels this long already cramp a vertical
// tick; a horizontal Y axis gives each its own upright line. Only cramps once
// there are several — a lone long label keeps the vertical (legend) path.
export const HORIZONTAL_LABEL_CHARS = 16;

/**
 * Decide when the bar chart should render horizontally (categories on the Y
 * axis) instead of vertically. True when there are more categories than
 * rotation can keep legible (> 8), OR when several categories (>= 4) each carry
 * a long label (> 16 chars) that would cramp a vertical tick. The `>= 4` guard
 * is deliberate: with 1-3 categories bars are wide and labels wrap horizontally
 * without collision, and it keeps a single long-labelled multi-measure chart on
 * the vertical (dual-axis legend) path.
 */
export function preferHorizontalBars(
  categoryCount: number,
  maxLabelChars: number
): boolean {
  if (categoryCount > HORIZONTAL_CATEGORY_THRESHOLD) return true;
  return categoryCount >= 4 && maxLabelChars > HORIZONTAL_LABEL_CHARS;
}

// Per-row vertical budget for a horizontal bar chart: each category gets its own
// row so 50 capped categories stay individually readable (the plot grows and
// scrolls rather than crushing the bands). MIN matches the h-96 vertical chart.
const MIN_CHART_PX = 384;
const ROW_PX = 28;
const AXIS_PAD_PX = 48;

/**
 * Height in px for a horizontal bar plot: grows with the category count so bands
 * stay readable, floored at the vertical chart's height so a small horizontal
 * chart isn't shorter than its vertical sibling. The caller renders this inside a
 * bounded, scrollable wrapper.
 */
export function horizontalChartHeight(categoryCount: number): number {
  return Math.max(MIN_CHART_PX, categoryCount * ROW_PX + AXIS_PAD_PX);
}

/**
 * Round a positive value up to a "nice" ceiling (1/2/2.5/5 × 10ⁿ) so an axis
 * domain lands on clean tick boundaries. Returns 0 for non-positive input.
 */
function niceCeil(value: number): number {
  if (value <= 0) return 0;
  const exponent = Math.floor(Math.log10(value));
  const base = 10 ** exponent;
  const fraction = value / base;
  const nice =
    fraction <= 1
      ? 1
      : fraction <= 2
        ? 2
        : fraction <= 2.5
          ? 2.5
          : fraction <= 5
            ? 5
            : 10;
  return nice * base;
}

/**
 * Explicit upper bound for a value axis: the largest single series value on that
 * axis (bars are grouped, not stacked, so a category's extent is its tallest
 * bar), rounded up to a nice ceiling. The horizontal chart renders the value
 * axes in a FIXED header that stays visible while the tall category plot scrolls
 * under it; the header ruler and the scrolling plot are two separate recharts
 * instances, so they must share one EXPLICIT domain — left to auto-fit, each
 * would pick its own max and the header ticks would drift out of alignment with
 * the bars. Returns 0 when the axis carries no numeric data (an empty result),
 * which lets recharts fall back to its own default rather than a [0,0] domain.
 */
export function valueAxisMax(
  rows: ResultRow[],
  series: ChartSeries[],
  axis: ChartAxisId
): number {
  let max = 0;
  for (const row of rows) {
    for (const s of series) {
      if (axisIdForKind(s.kind) !== axis) continue;
      const value = row[s.key];
      if (typeof value === 'number' && value > max) max = value;
    }
  }
  return niceCeil(max);
}

/**
 * Evenly spaced tick values [0 … max] (count + 1 of them) for the horizontal
 * chart's fixed value ruler. The same array is handed to the scrolling plot's
 * hidden value axis so bars, gridlines, and the header ruler all key off one
 * scale. Falls back to [0] for a non-positive max (an empty result).
 */
export function axisTicks(max: number, count = 4): number[] {
  if (max <= 0 || count < 1) return [0];
  return Array.from({ length: count + 1 }, (_, i) => (max * i) / count);
}

/**
 * Character budget per horizontal tick line. Fewer categories leave more width
 * per bar, so longer text fits before wrapping/truncating; this keeps a small
 * two-category chart from clipping distinct long labels to the same stub.
 */
export function axisCharBudget(categoryCount: number): number {
  if (categoryCount <= 2) return 24;
  if (categoryCount <= 4) return 18;
  if (categoryCount <= 6) return 14;
  return 12;
}

/**
 * Wrap a composite category label into display lines for a horizontal axis tick.
 * Splits first on the composite separator (each group-by dimension on its own
 * line — "0-30" over "UnitedHealthcare…"), then word-wraps any segment wider
 * than `maxChars`. Caps at `maxLines`; the last kept line is ellipsis-truncated
 * only when content remains, so distinct labels keep as much distinguishing text
 * as the width allows (the full value is always in the tooltip).
 */
export function wrapLabelLines(
  label: string,
  maxChars: number,
  maxLines = 2
): string[] {
  const lines: string[] = [];
  const pushWrapped = (text: string) => {
    let current = '';
    for (const word of text.split(' ')) {
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length <= maxChars || current === '') {
        current = candidate;
      } else {
        lines.push(current);
        current = word;
      }
    }
    if (current) lines.push(current);
  };
  for (const segment of label.split(CATEGORY_SEP)) pushWrapped(segment);

  const shown =
    lines.length <= maxLines
      ? lines
      : [...lines.slice(0, maxLines - 1), lines.slice(maxLines - 1).join(' ')];
  return shown.map(line => truncateLabel(line, maxChars));
}

/**
 * Count the leading group-by dimensions that every displayed category shares —
 * e.g. rows [["0-30","Aetna"],["0-30","Cigna"]] share one dimension ("0-30").
 * Each row carries its own per-dimension formatted values, so shared-ness is
 * compared dimension by dimension and never by splitting the composite label
 * (whose CATEGORY_SEP can also occur inside a single value, e.g. a region named
 * "North · East"). A shared leading dimension is redundant on every tick, so the
 * axis drops it (the full label still shows in the tooltip) and it surfaces once
 * above the plot. Never counts the last dimension, so one always survives.
 */
export function sharedLeadingDimensions(rows: string[][]): number {
  if (rows.length < 2) return 0;
  const dimCount = Math.min(...rows.map(r => r.length));
  let shared = 0;
  for (let i = 0; i < dimCount - 1; i++) {
    if (rows.every(r => r[i] === rows[0][i])) shared++;
    else break;
  }
  return shared;
}
